import { useEffect, useState } from "react";
import { Award, Lock } from "lucide-react";
import SetupHelperBadge from "@/pages/SetupHelperBadge";
import SetupHelperDialog from "@/pages/SetupHelperDialog";
import { api } from "@/lib/api";

export const TIERS = {
  10: { label: "Rising Seller", ring: "from-sky-400 to-blue-600" },
  50: { label: "Hustler", ring: "from-emerald-400 to-teal-600" },
  100: { label: "Century Club", ring: "from-amber-300 to-orange-500" },
  250: { label: "Power Seller", ring: "from-fuchsia-400 to-purple-600" },
  500: { label: "Top Shop", ring: "from-rose-400 to-red-600" },
  1000: { label: "Legend", ring: "from-yellow-300 via-amber-400 to-yellow-600" },
};

export function MilestoneBadge({ n, earned, size = "md" }) {
  const t = TIERS[n] || { label: "", ring: "from-slate-300 to-slate-500" };
  const dim = size === "lg" ? "h-24 w-24 text-3xl" : size === "row" ? "h-11 w-11 text-sm sm:h-14 sm:w-14 sm:text-base" : "h-14 w-14 text-base";
  return (
    <div className={`relative flex ${dim} shrink-0 items-center justify-center rounded-full p-[3px] ${earned ? `bg-gradient-to-br ${t.ring} shadow-md` : "bg-slate-200"}`}
      data-testid={`milestone-badge-${n}`} data-earned={earned ? "true" : "false"}>
      <div className={`flex h-full w-full items-center justify-center rounded-full font-bold ${earned ? "bg-white text-slate-900" : "bg-slate-50 text-slate-400"}`}>
        {earned ? n : <Lock className="h-4 w-4" />}
      </div>
    </div>
  );
}

export default function MilestonesRow() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/milestones").then((r) => setData(r.data)).catch(() => {}); }, []);
  if (!data) return null;

  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5" data-testid="milestones-row">
      <div className="mb-4 flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="flex items-center gap-2 text-base font-semibold text-slate-900"><Award className="h-4 w-4 text-amber-500" /> Milestones</h2>
        <p className="text-xs text-slate-500" data-testid="milestones-progress">
          {data.sold_count} sold{data.next ? ` · ${data.next - data.sold_count} to ${data.next}` : " · all badges earned"}
        </p>
      </div>
      <div className="grid grid-cols-6 gap-1 sm:flex sm:gap-4">
        {data.milestones.map((m) => (
          <div key={m.n} className="flex min-w-0 flex-col items-center gap-1.5 text-center sm:w-20 sm:shrink-0">
            <MilestoneBadge n={m.n} earned={m.earned} size="row" />
            <span className={`text-[10px] leading-tight sm:text-[11px] ${m.earned ? "font-medium text-slate-800" : "text-slate-400"}`}>{TIERS[m.n].label}</span>
          </div>
        ))}
      </div>
      <SetupHelperBadge helper={data.setup_helper} />
      {data.setup_helper?.celebrate && (
        <SetupHelperDialog open sites={data.setup_helper.sites} onClose={() => {
          api.post("/milestones/helper-ack").catch(() => {});
          setData((d) => ({ ...d, setup_helper: { ...d.setup_helper, celebrate: false } }));
        }} />
      )}
    </section>
  );
}
